import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchPushnotifications,
    createPushNotification,
    updatePushNotification,
    deletePushNotification,
    setPagination,
    setSort,
    searchPushnotifications,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    resetFiltersAndPagination
} from '../../store/modules/pushnotification/pushnotificationActions';
import PushNotificationForm from '../../components/pageModule/Pushnotifications/PushNotificationForm';

const MySwal = withReactContent(Swal);

const Pushnotifications = ({ excludedActions = [], onSelectedRowsChange = () => { } }) => {

    const pushnotificationState = useSelector(state => state.pushnotification);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createPushNotification(formData));
            toast.success('PushNotification created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating pushnotification', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updatePushNotification(id, formData));
            toast.success('PushNotification updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating pushnotification', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deletePushNotification(id));
                toast.success('PushNotification deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting pushnotification', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const columns = [
        {
            name: 'Title',
            selector: row => row.title?.en ?? '-',
            sortable: true,
            sortField: 'title',
        },
        {
            name: 'Message',
            selector: row => row.message?.en ?? '-',
            sortable: true,
            sortField: 'message',
        },
        {
            name: 'Scheduled',
            selector: row => row.scheduled_at ? new Date(row.scheduled_at).toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            }) : 'No',
        },
        {
            name: 'Notification Sent',
            selector: row => row.sent_at ? (
                <span className="badge bg-success">
                    {new Date(row.sent_at).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true
                    })}
                </span>
            ) : <span className="badge bg-danger">No</span>,
        }
    ];

    const actions = {
        fetch: (queryFilter, activeFilter) => dispatch(fetchPushnotifications(queryFilter, activeFilter)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchPushnotifications(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter))
    };

    const defaultAvailableActions = ['create', 'search', 'actions', 'edit'];
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Push Notifications"
                moduleName="PushNotification"
                moduleKey="pushnotifications"
                state={pushnotificationState}
                actions={actions}
                FormComponent={PushNotificationForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                onSelectedRowsChange={onSelectedRowsChange}
            />
        </>
    );
};

export default Pushnotifications; 